/* eslint-disable react-hooks/exhaustive-deps */
import { ReactNode, useCallback, useEffect, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { Alert, Button, Col, InputNumber, Modal, Row, Spin, Table, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { CloseOutlined } from '@ant-design/icons'

import { config } from '@config'
import { CarrierAvailability } from '@types'
import { QUERY_KEYS } from '@api'
import { useUser } from '@contexts'

import { DATE_KEYS } from '../../../../constants/dateKeys'
import { preventEvent, limitIntEntry } from '../../../../utils/inputs'
import { useGetCarrierAvailability } from '../../../../hooks/carrierAvailability/useGetCarrierAvailability'
import { useDeclareCarrierAvailability } from '../../../../hooks/carrierAvailability/useDeclareCarrierAvailability'
import { ContentContainer, FlexCol } from '../../../../components/StyledComponents'

const now = dayjs()
const limitRangeErrorInSeconds = 1

const calculateCount = (dateSetter: () => void) => {
  const tomorrow = dayjs()
  tomorrow
    .set('hour', config.availabilityDeadline - 1)
    .set('minute', 45)
    .set('second', 0)
  if (now.hour() >= config.availabilityDeadline) {
    tomorrow.add(1, 'day')
  }
  dateSetter()
  return tomorrow.diff(now, 'seconds')
}

const Availability = () => {
  const { t } = useTranslation()
  const { state: user } = useUser()
  const [count, setCount] = useState<number>()
  const [date, setDate] = useState<Dayjs>()
  const [data, setData] = useState<CarrierAvailability[]>()
  const [availabilityMsg, setAvailabilityMsg] = useState<ReactNode>()
  const [adviceMsg, setAdviceMsg] = useState<ReactNode>()
  const [confirmModalIsOpen, setConfirmModalIsOpen] = useState<boolean>()
  const {
    carrierAvailabilityData,
    carrierAvailabilityDataIsLoading,
    refetchCarrierAvailabilityData,
  } = useGetCarrierAvailability()
  const [declarationDone, setDeclarationDone] = useState<boolean>()
  const queryClient = useQueryClient()

  const resetData = useCallback(() => {
    setData(carrierAvailabilityData?.declarations ?? [])
  }, [carrierAvailabilityData])

  const reset = () => {
    setConfirmModalIsOpen(false)
    refetchCarrierAvailabilityData()
    resetData()
    queryClient.invalidateQueries(QUERY_KEYS.CARRIER_AVAILABILITY)
  }

  const { declareCarrierAvailability, declareCarrierAvailabilityIsLoading } =
    useDeclareCarrierAvailability(reset)

  const handleDeclare = () => {
    setConfirmModalIsOpen(true)
  }

  const handleQuantityChange = (quantity: number, equipmentId: number) => {
    setData(prevState =>
      prevState?.map(item => {
        if (item.equipment.id === equipmentId) {
          return { equipment: item.equipment, quantity }
        }
        return item
      }),
    )
  }

  const dateSetter = useCallback(() => {
    const auxDayjs = dayjs().utcOffset(user!.company.gmtZone, true)
    setDate(
      dayjs()
        .utcOffset(user!.company.gmtZone, true)
        .add(
          auxDayjs.add(limitRangeErrorInSeconds, 's').hour() >= config.availabilityDeadline ? 2 : 1,
          'd',
        ),
    )
  }, [user])

  const declare = useCallback(() => {
    if (data && date) {
      declareCarrierAvailability({
        carrierAvailability: {
          date: date.format(DATE_KEYS.FORMAT.ISO),
          declarations: data.map(({ equipment, quantity }) => ({
            equipmentId: equipment.id,
            quantity: quantity ?? 0,
          })),
        },
      })
      reset()
    }
  }, [data, date])

  const countdownSetter = () => {
    setCount(state =>
      state && state >= limitRangeErrorInSeconds ? state - 1 : calculateCount(dateSetter),
    )
  }

  useEffect(() => {
    const interval = setInterval(countdownSetter, 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (carrierAvailabilityData?.declarations) {
      resetData()
    }
    setDeclarationDone(carrierAvailabilityData?.declarations[0].quantity !== null)
  }, [carrierAvailabilityData])

  useEffect(() => {
    if (count !== undefined && count <= limitRangeErrorInSeconds) {
      reset()
      setCount(calculateCount(dateSetter))
      dateSetter()
    }
  }, [count])

  useEffect(() => {
    if (date) {
      setAvailabilityMsg(
        <Typography style={{ fontSize: '1.125rem' }}>
          Declaración disponibilidad para la fecha:{' '}
          <strong style={{ whiteSpace: 'nowrap' }}>
            {date.format(DATE_KEYS.FORMAT.DAY_SHORT_YEAR)}
          </strong>
          {` (${user?.company.shortName})`}
        </Typography>,
      )
      setAdviceMsg(
        <Typography>
          Se podrá hacer la declaración del día siguiente hasta las {config.availabilityDeadline}hs.{' '}
          <strong>Una vez realizado no podrá modificarse</strong>
        </Typography>,
      )
    }
  }, [date])

  return (
    <ContentContainer className="availability-declaration">
      {declarationDone !== undefined &&
      data &&
      date &&
      carrierAvailabilityData &&
      !carrierAvailabilityDataIsLoading ? (
        <>
          <Col style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {availabilityMsg}
            <Alert
              message={
                declarationDone
                  ? `Podrás hacer una nueva declaración a las ${
                      config.availabilityDeadline
                    }:00 del ${date.clone().add(-1, 'days').format('DD/MM/YYYY')}`
                  : adviceMsg
              }
              type="warning"
              showIcon
              className="fit-alert"
              style={{ width: 'fit-content' }}
            />
          </Col>
          <Col style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '64rem' }}>
            <Table
              columns={[
                {
                  title: 'Equipo',
                  dataIndex: 'equipment',
                  render: equipment => equipment.nameShort,
                },
                {
                  title: t('carrier_availability.available_quantity') as string,
                  dataIndex: 'quantity',
                  width: '10rem',
                  render: (quantity, row) => (
                    <Row style={{ justifyContent: 'center' }}>
                      <InputNumber
                        disabled={declarationDone}
                        min={0}
                        max={1000}
                        value={quantity ?? 0}
                        style={{ textAlign: 'right' }}
                        onChange={value => {
                          handleQuantityChange(value ?? 0, row.equipment.id)
                        }}
                        onPaste={preventEvent}
                        onKeyDown={limitIntEntry}
                        autoComplete="no"
                      />
                    </Row>
                  ),
                },
              ]}
              dataSource={data}
              pagination={false}
            />
            <Row style={{ gap: '1rem', justifyContent: 'flex-end', margin: '0 2.2rem' }}>
              <Button
                onClick={reset}
                disabled={declarationDone || !data.some(({ quantity }) => !!quantity)}
              >
                {t('globals.generics.reset')}
              </Button>
              <Button onClick={handleDeclare} type="primary" disabled={declarationDone}>
                {t('globals.generics.confirm')}
              </Button>
            </Row>
          </Col>
        </>
      ) : (
        <Spin />
      )}
      <Modal
        open={confirmModalIsOpen}
        keyboard
        maskClosable
        onCancel={() => {
          setConfirmModalIsOpen(false)
        }}
        okText={t('globals.generics.confirm')}
        cancelText={t('globals.generics.cancel')}
        onOk={declare}
        destroyOnClose
        confirmLoading={declareCarrierAvailabilityIsLoading}
        closeIcon={<CloseOutlined />}
        width={640}
      >
        <FlexCol style={{ gap: '3rem', padding: '3rem', textAlign: 'center' }}>
          {availabilityMsg}
          {adviceMsg}
        </FlexCol>
      </Modal>
    </ContentContainer>
  )
}

export default Availability
